import React, { useState, useEffect } from "react";
import { Box, Stack } from '@mui/material';
import DataTable from "../../../../../../component/DataTable";
import MainBox from "../../../../container/MainBox";
import { FilterSelect, StatusChip, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { getAllPayslipQueries } from "../../../../../../services/payroll";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";
import { periodLabel } from "../payrollUtils";
import QueryThread from "./QueryThread";

const STATUS_TONE = { open: "warning", under_review: "info", resolved: "success", rejected: "danger" };
const STATUS_LABEL = { open: "Open", under_review: "Under review", resolved: "Resolved", rejected: "Rejected" };
const nameOf = (q) => q.employee ? `${q.employee.fname || ""} ${q.employee.lname || ""}`.trim() : "—";

const PayslipQueries = () => {
  const notify = useNotify();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState("");
  const [active, setActive] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getAllPayslipQueries(status);
      if (res?.success === false) throw new Error(res?.message || "Failed to load queries");
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(true);
      notify.error(e?.message || "Failed to load queries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [status]);

  return (
    <MainBox sx={{ padding: "1.25rem" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Payslip Queries</Box>
        <FilterSelect value={status} onChange={(e) => setStatus(e.target.value)}
          options={[{ value: "", label: "All statuses" }, { value: "open", label: "Open" }, { value: "under_review", label: "Under review" }, { value: "resolved", label: "Resolved" }, { value: "rejected", label: "Rejected" }]} />
      </Stack>

      <DataTable
        rows={rows}
        loading={loading}
        error={error}
        onRetry={fetchData}
        errorTitle="Couldn't load queries"
        empty={{ title: "No payslip queries", description: "Employee concerns on payslips will appear here." }}
        onRowClick={(q) => setActive(q)}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        columns={[
          { key: "employee", label: "Employee", width: "24%", cellSx: { fontWeight: 600 }, render: (q) => nameOf(q) },
          {
            key: "subject",
            label: "Subject",
            cellSx: { color: tokens.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
            render: (q) => q.subject,
          },
          { key: "period", label: "Period", cellSx: { color: tokens.textMuted }, render: (q) => periodLabel(q.month, q.year) },
          {
            key: "status",
            label: "Status",
            render: (q) => <StatusChip label={STATUS_LABEL[q.status] || q.status} tone={STATUS_TONE[q.status]} />,
          },
        ]}
      />

      <QueryThread open={Boolean(active)} query={active} isAdmin onClose={() => setActive(null)}
        onChanged={(updated) => { setActive(updated); fetchData(); }} />
    </MainBox>
  );
};

export default PayslipQueries;
